import { useEffect, useState, useCallback, useMemo } from "react";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { EntityRecord } from "@/types/entity";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

// Constants from HasNotifyBaseMixin.jsx
export const NOTIFY_TYPE_RECORD_KEYS = {
  reminder: "isReminderUpdates",
  assignment: "assignedBoardUsers",
  incentive: "eligibleBoardUsers",
  general: "isGeneralUpdates",
  notes: "isNotesUpdates",
  schedule: "isScheduleUpdates",
  checklist: "isChecklistUpdates",
  files: "isFilesUpdates",
} as const;

export type NotifyType = keyof typeof NOTIFY_TYPE_RECORD_KEYS;

const STORE_BY_NOTIFY_TYPE_KEY_MAP = {
  [NOTIFY_TYPE_RECORD_KEYS.general]: STORE_KEYS.notifyeeBoardUsers,
  [NOTIFY_TYPE_RECORD_KEYS.notes]: STORE_KEYS.notifyeeBoardUsers,
  [NOTIFY_TYPE_RECORD_KEYS.schedule]: STORE_KEYS.notifyeeBoardUsers,
  [NOTIFY_TYPE_RECORD_KEYS.checklist]: STORE_KEYS.notifyeeBoardUsers,
  [NOTIFY_TYPE_RECORD_KEYS.reminder]: STORE_KEYS.notifyeeBoardUsers,
  [NOTIFY_TYPE_RECORD_KEYS.files]: STORE_KEYS.notifyeeBoardUsers,
  [NOTIFY_TYPE_RECORD_KEYS.incentive]: STORE_KEYS.resourceStore,
  [NOTIFY_TYPE_RECORD_KEYS.assignment]: STORE_KEYS.resourceStore,
} as const;

export interface OwnershipData {
  type: string;
  boardUserIds: number[];
}

export interface BoardUser {
  id: number;
  name: string;
  profileImageURL?: string;
  archived?: boolean;
  isDeactivated?: boolean;
}

interface UseEntityOwnershipProps {
  record: EntityRecord | null | undefined;
  excludeTypes?: string[];
}

export type TUseEntityOwnershipReturn = ReturnType<typeof useEntityOwnership>;

// Stable default: `excludeTypes = []` allocates a new array every render and
// retriggers the store subscription effect via updateOwnershipData.
export const EMPTY_EXCLUDE_TYPES: string[] = [];

export const useEntityOwnership = ({
  record,
  excludeTypes = EMPTY_EXCLUDE_TYPES,
}: UseEntityOwnershipProps) => {
  const { project } = useSwivuBryntumContext();
  const projectTyped = project as unknown as {
    boardUsersStore: {
      query: (fn: (record: BoardUser) => boolean, returnAll: boolean) => Array<BoardUser & Record<string, unknown>>;
    };
    getCrudStore: (key: string) => {
      id: string;
      query: (fn: (record: Record<string, unknown>) => boolean, returnAll: boolean) => Array<Record<string, unknown>>;
      find: (fn: (record: Record<string, unknown>) => boolean) => Record<string, unknown> | undefined;
      createRecord: (data: Record<string, unknown>) => Record<string, unknown>;
      add: (record: Record<string, unknown>) => void;
      on: (event: string, handler: () => void) => () => void;
    };
  };

  const [ownershipData, setOwnershipData] = useState<OwnershipData[]>([]);

  const recordType = record?.entityType;

  // Get all board users for the selector (memoized for performance)
  const boardUsers = useMemo<BoardUser[]>(() => {
    return projectTyped?.boardUsersStore
      ?.query((record: BoardUser) => !record.isDeactivated, true)
      .map((user) => ({
        id: user.id,
        name: user.name,
        profileImageURL: user.profileImageURL,
        archived: user.archived,
      })) || [];
  }, [projectTyped]);

  // Get board user IDs for a specific notify type (ported from lines 181-189)
  const getBoardUserValueByNotifyType = useCallback((type: string): number[] => {
    if (!projectTyped || !record || !recordType) return [];

    const storeKey = STORE_BY_NOTIFY_TYPE_KEY_MAP[type as keyof typeof STORE_BY_NOTIFY_TYPE_KEY_MAP];
    const store = projectTyped.getCrudStore(storeKey);

    if (store.id === STORE_KEYS.resourceStore) {
      // For assignment/incentive: direct array on record
      const value = record[type as keyof typeof record];
      return Array.isArray(value) ? value : [];
    } else {
      // For other types: query notifyeeBoardUsers store
      const boardUserIds = store
        .query((notifyRecord) =>
          notifyRecord[recordType] === record.id && notifyRecord[type] === true,
          true
        )
        .map((notifyBoardUserRecord) => notifyBoardUserRecord.boardUserId as number);
      return boardUserIds;
    }
  }, [projectTyped, record, recordType]);

  // Update ownership data
  const updateOwnershipData = useCallback(() => {
    const data: OwnershipData[] = [];

    for (const key in NOTIFY_TYPE_RECORD_KEYS) {
      const recordKey = NOTIFY_TYPE_RECORD_KEYS[key as keyof typeof NOTIFY_TYPE_RECORD_KEYS];

      // Skip excluded types
      if (excludeTypes.includes(recordKey)) continue;

      data.push({
        type: recordKey,
        boardUserIds: getBoardUserValueByNotifyType(recordKey),
      });
    }

    setOwnershipData(data);
  }, [getBoardUserValueByNotifyType, excludeTypes]);

  // Handle user add/remove (ported from lines 278-296: syncSwivuRecordUpdate)
  const syncRecordUpdate = useCallback(
    async ({
      notifyType,
      newBoardUserIds,
      action,
    }: {
      notifyType: string;
      newBoardUserIds: number[];
      action: "add" | "remove";
    }) => {
      if (!projectTyped || !record || !recordType) return;

      const storeKey = STORE_BY_NOTIFY_TYPE_KEY_MAP[notifyType as keyof typeof STORE_BY_NOTIFY_TYPE_KEY_MAP];
      const store = projectTyped.getCrudStore(storeKey);

      if (store.id === STORE_KEYS.resourceStore) {
        // Direct assignment for assignment/incentive
        (record as Record<string, unknown>)[notifyType] = newBoardUserIds;
      } else {
        // Handle notifyeeBoardUsers store
        newBoardUserIds.forEach((boardUserId) => {
          const notifyBoardUserRecord = store.find(
            (rec) =>
              rec.boardUserId === boardUserId && rec[recordType] === record.id
          );

          if (notifyBoardUserRecord) {
            // Update existing record
            notifyBoardUserRecord[notifyType] = action === "add";
          } else if (action === "add") {
            // Create new record
            const newRecord = store.createRecord({
              [notifyType]: true,
              boardUserId: boardUserId,
              [recordType]: record.id,
            });
            store.add(newRecord);
          }
        });

        // Remove users that are no longer in the list
        const existingRecords = store.query(
          (rec) => rec[recordType] === record.id,
          true
        );

        existingRecords.forEach((existingRecord) => {
          if (!newBoardUserIds.includes(existingRecord.boardUserId as number)) {
            existingRecord[notifyType] = false;
          }
        });
      }
    },
    [projectTyped, record, recordType]
  );

  // Handle user selection change
  const handleUserSelectionChange = useCallback(
    async (notifyType: string, selectedBoardUserIds: number[]) => {
      await syncRecordUpdate({
        notifyType,
        newBoardUserIds: selectedBoardUserIds,
        action: "add", // The syncRecordUpdate handles both add and remove based on the new list
      });
    },
    [syncRecordUpdate]
  );

  // Listen to store changes (ported from lines 221-227)
  useEffect(() => {
    if (!projectTyped) return;

    const listeners: (() => void)[] = [];

    // Listen to both stores
    [STORE_KEYS.notifyeeBoardUsers, STORE_KEYS.resourceStore].forEach((storeId) => {
      const store = projectTyped.getCrudStore(storeId);
      const listener = store.on("change", updateOwnershipData);
      listeners.push(listener);
    });

    // Initial data load
    updateOwnershipData();

    // Cleanup
    return () => {
      listeners.forEach((listener) => listener());
    };
  }, [projectTyped, updateOwnershipData]);

  return {
    ownershipData,
    boardUsers,
    handleUserSelectionChange,
  };
};